import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import { useToast } from '../ui/ToastProvider'
import {
  CLAIM_INSURER_CATEGORY_FILTERS,
  claimInsurerCategoryLabel,
  matchesClaimInsurerCategory,
  useClaimAdminInsurers,
} from '../hooks/useClaimAdminInsurers'
import MenuGuideButton from '../components/MenuGuideButton'
import { buildClaimFaxRoutingPayload, parseClaimFaxRoutingFields } from '../lib/claimFaxRouting'
import InsurerFaxSettingCells from '../components/insurerMaster/InsurerFaxSettingCells'

function digitsKey(s) {
  return String(s || '').replace(/\D/g, '')
}

export default function ClaimInsurerFaxSettingsPage({ embedded = false }) {
  const { show } = useToast()
  const {
    allInsurers,
    lifeRootId,
    nonlifeRootId,
    petRootId,
    liabilityRootId,
    loading: insurersLoading,
  } = useClaimAdminInsurers()
  const [categoryFilter, setCategoryFilter] = useState('ALL')
  const [rows, setRows] = useState({})
  const [settingsLoading, setSettingsLoading] = useState(true)
  const [savingId, setSavingId] = useState(null)

  const filteredInsurers = useMemo(() => {
    if (categoryFilter === 'ALL') return allInsurers
    return allInsurers.filter((o) =>
      matchesClaimInsurerCategory(o, categoryFilter, lifeRootId, nonlifeRootId, petRootId, liabilityRootId))
  }, [allInsurers, categoryFilter, lifeRootId, nonlifeRootId, petRootId, liabilityRootId])

  const loadSettings = useCallback(async () => {
    const res = await api.get('/claims/insurer-fax-settings')
    const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
    const list = Array.isArray(payload) ? payload : []
    const map = {}
    for (const row of list) {
      const id = row?.insurerOrgId
      if (id == null) continue
      const { faxRoutingDisease, faxRoutingInjury } = parseClaimFaxRoutingFields(row.claimFaxRoutingJson)
      map[String(id)] = {
        claimFaxNumber: row.claimFaxNumber || '',
        virtualFaxRequired: !!row.virtualFaxRequired,
        memo: row.memo || '',
        faxRoutingDisease,
        faxRoutingInjury,
      }
    }
    setRows(map)
  }, [])

  const refresh = useCallback(async () => {
    setSettingsLoading(true)
    try {
      await loadSettings()
    } catch {
      show('설정을 불러오지 못했습니다.', 'error')
    } finally {
      setSettingsLoading(false)
    }
  }, [loadSettings, show])

  useEffect(() => {
    refresh()
  }, [refresh])

  const merged = useMemo(() => {
    return filteredInsurers.map((o) => {
      const id = String(o.id)
      const r = rows[id] || {
        claimFaxNumber: '',
        virtualFaxRequired: false,
        memo: '',
        faxRoutingDisease: '',
        faxRoutingInjury: '',
      }
      return { org: o, ...r }
    })
  }, [filteredInsurers, rows])

  const updateRow = (orgId, patch) => {
    const id = String(orgId)
    setRows((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {
          claimFaxNumber: '',
          virtualFaxRequired: false,
          memo: '',
          faxRoutingDisease: '',
          faxRoutingInjury: '',
        }),
        ...patch,
      },
    }))
  }

  const save = async (orgId) => {
    const id = String(orgId)
    const r = rows[id] || {
      claimFaxNumber: '',
      virtualFaxRequired: false,
      memo: '',
      faxRoutingDisease: '',
      faxRoutingInjury: '',
    }
    const hasRef =
      !!digitsKey(r.claimFaxNumber) || !!digitsKey(r.faxRoutingDisease) || !!digitsKey(r.faxRoutingInjury)
    if (r.virtualFaxRequired && !hasRef) {
      show('가상팩스 필수 보험사는 비교용 안내 번호(기본 또는 질병/상해계) 중 하나 이상을 입력해야 합니다.', 'error')
      return
    }
    setSavingId(id)
    try {
      await api.put(`/claims/insurer-fax-settings/${orgId}`, {
        claimFaxNumber: r.claimFaxNumber?.trim() || null,
        virtualFaxRequired: !!r.virtualFaxRequired,
        memo: r.memo?.trim() || null,
        claimFaxRoutingJson: buildClaimFaxRoutingPayload(r.faxRoutingDisease, r.faxRoutingInjury),
      })
      show('저장했습니다.', 'success')
      await loadSettings()
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error?.message || ''
      show(msg || '저장에 실패했습니다.', 'error')
    } finally {
      setSavingId(null)
    }
  }

  const loading = insurersLoading || settingsLoading

  return (
    <div className="space-y-4">
      {!embedded ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold">청구번호 (보험사별)</h1>
              <MenuGuideButton slug="claims" title="보험금청구 가이드" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link className="rounded border px-3 py-2 text-sm hover:bg-slate-50" to="/ops/forms/claims/templates">
              청구서식
            </Link>
            <Link className="rounded border px-3 py-2 text-sm hover:bg-slate-50" to="/ops/stats/claims">
              청구통계
            </Link>
            <Link className="rounded border px-3 py-2 text-sm hover:bg-slate-50" to="/support/claims">
              보험금 청구
            </Link>
          </div>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-slate-700">구분</span>
        {CLAIM_INSURER_CATEGORY_FILTERS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={`rounded border px-3 py-1.5 text-sm ${
              categoryFilter === key ? 'border-brand-navy bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10' : 'bg-white hover:bg-slate-50'
            }`}
            onClick={() => setCategoryFilter(key)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="rounded border bg-white overflow-x-auto">
        <table className="min-w-[1100px] w-full text-sm">
          <thead>
            <tr className="border-b bg-slate-50 text-left">
              <th className="px-3 py-2 font-semibold">보험사</th>
              <th className="px-3 py-2 font-semibold w-14">구분</th>
              <th className="px-3 py-2 font-semibold w-[180px]">기본 안내 팩스</th>
              <th className="px-3 py-2 font-semibold w-[160px]">질병 안내 팩스</th>
              <th className="px-3 py-2 font-semibold w-[160px]">상해계 안내 팩스</th>
              <th className="px-3 py-2 font-semibold w-[120px]">가상팩스 필수</th>
              <th className="px-3 py-2 font-semibold">메모</th>
              <th className="px-3 py-2 font-semibold w-[100px]" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-slate-500">불러오는 중…</td>
              </tr>
            )}
            {!loading && merged.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-slate-500">해당 구분에 표시할 보험사가 없습니다.</td>
              </tr>
            )}
            {!loading && merged.map((row) => (
              <tr key={row.org.id} className="border-b last:border-b-0 align-top">
                <td className="px-3 py-2 font-medium text-slate-900">{row.org.name}</td>
                <td className="px-3 py-2 text-slate-600">
                  {claimInsurerCategoryLabel(row.org, lifeRootId, nonlifeRootId, petRootId, liabilityRootId)}
                </td>
                <InsurerFaxSettingCells
                  value={row}
                  saving={savingId === String(row.org.id)}
                  onChange={(patch) => updateRow(row.org.id, patch)}
                  onSave={() => save(row.org.id)}
                />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
